import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  children: ReactNode
  /** Etichetta della sezione mostrata nel messaggio di errore (es. "Executive Cockpit") */
  section?: string
  /** Fallback custom — se omesso usa il fallback di default */
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

// ─────────────────────────────────────────────────────────────────────────────
// Default fallback UI
// ─────────────────────────────────────────────────────────────────────────────

function DefaultFallback({
  section,
  onReset,
}: {
  section?: string
  onReset: () => void
}) {
  return (
    <div
      role="alert"
      aria-live="assertive"
      className="flex flex-col items-center justify-center gap-6 rounded-xl border border-red-500/20 bg-red-500/5 p-12 text-center"
    >
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-500/10">
        <AlertTriangle size={28} className="text-[--km-error]" aria-hidden="true" />
      </div>

      <div className="space-y-2">
        <p className="text-base font-semibold text-[--km-text-primary]">
          {section ? `Errore nella sezione "${section}"` : 'Si è verificato un errore'}
        </p>
        <p className="text-sm text-[--km-text-muted] max-w-md">
          Qualcosa è andato storto nel caricamento di questa sezione.
          Prova a ricaricare — se il problema persiste, contatta il supporto.
        </p>
      </div>

      <button
        onClick={onReset}
        className="flex items-center gap-2 rounded-lg bg-[--km-surface-2] border border-[--km-border] px-5 py-2.5 text-sm font-medium text-[--km-text-secondary] transition-colors hover:border-[--km-border-hover] hover:text-[--km-text-primary]"
      >
        <RefreshCw size={14} aria-hidden="true" />
        Riprova
      </button>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// ErrorBoundary class component
// ─────────────────────────────────────────────────────────────────────────────

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Log interno — mai esporre stack trace all'utente
    console.error('[ErrorBoundary] Uncaught error:', error, info.componentStack)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }
      return (
        <DefaultFallback section={this.props.section} onReset={this.handleReset} />
      )
    }

    return this.props.children
  }
}
